/**
 * Design System - Chip Component
 * Componente chip standardizzato per flag e stati
 */

import React from 'react';
import { Chip as MuiChip, ChipProps as MuiChipProps } from '@mui/material';
import { styled } from '@mui/material/styles';

// Varianti personalizzate
export type ChipVariant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'outlined';
export type ChipSize = 'small' | 'medium';

export interface ChipProps extends Omit<MuiChipProps, 'variant' | 'size' | 'color'> {
  variant?: ChipVariant;
  size?: ChipSize;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

// Styled component per varianti personalizzate
const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) => !['customVariant'].includes(prop as string),
})<ChipProps & { customVariant?: ChipVariant }>(({ theme, customVariant = 'default', size = 'medium' }) => {
  const getVariantStyles = () => {
    switch (customVariant) {
      case 'success':
        return {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
          border: '1px solid #a5d6a7',
        };
      case 'error':
        return {
          backgroundColor: '#ffebee',
          color: '#c62828',
          border: '1px solid #ef9a9a',
        };
      case 'warning':
        return {
          backgroundColor: '#fff8e1',
          color: '#f57c00',
          border: '1px solid #ffcc02',
        };
      case 'info':
        return {
          backgroundColor: '#e1f5fe',
          color: '#0277bd',
          border: '1px solid #81d4fa',
        };
      case 'outlined':
        return {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
        };
      default:
        return {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.grey[300]}`,
        };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          height: '24px',
          fontSize: '0.75rem',
          padding: '0 8px',
          '& .MuiChip-label': {
            padding: '0 4px',
          },
          '& .MuiChip-icon': {
            fontSize: '0.75rem',
            margin: '0 2px 0 -4px',
          },
          '& .MuiChip-deleteIcon': {
            fontSize: '0.75rem',
            margin: '0 -4px 0 2px',
          },
        };
      case 'medium':
        return {
          height: '32px',
          fontSize: '0.875rem',
          padding: '0 12px',
          '& .MuiChip-label': {
            padding: '0 6px',
          },
          '& .MuiChip-icon': {
            fontSize: '0.875rem',
            margin: '0 4px 0 -6px',
          },
          '& .MuiChip-deleteIcon': {
            fontSize: '0.875rem',
            margin: '0 -6px 0 4px',
          },
        };
      default:
        return {};
    }
  };

  return {
    ...getVariantStyles(),
    ...getSizeStyles(),
    borderRadius: theme.shape.borderRadius,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: customVariant === 'outlined' 
        ? theme.palette.grey[50] 
        : undefined,
    },
  };
});

export const Chip: React.FC<ChipProps> = ({
  variant = 'default',
  size = 'medium',
  ...props
}) => {
  return (
    <StyledChip
      {...props}
      customVariant={variant}
      size={size}
    />
  );
};

export default Chip;
